import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    Home,
    TrendingUp,
    X,
    ChevronDown,
    ChevronRight,
    Check,
    Building2,
    Building,
    Map,
    Trees,
    Layers,
} from "lucide-react";

const ManualSearchOverlay = ({
    showFilters,
    setShowFilters,
    buyerType,
    setBuyerType,
    advFilters,
    setAdvFilters,
    filters,
    setFilters,
    onClear, // Added onClear prop
    onApply, // Optional: called when "Apply Filters" is clicked (before closing)
    // Opt-out flags so a page can reuse this panel with only the controls its
    // data supports (defaults keep the full panel for existing callers).
    showBuyerType = true,
    showSaleMethod = true,
    showPriceOnlyToggle = true,
    // Off on the Public Property Search, where the search bar above already
    // takes the suburb (Christian, Monday 12573204628, 12 Oct 2026).
    showLocationSearch = true,
    subtitle = "Your filter options will adjust based on your buyer type.",
}) => {
    const [overlayPosition, setOverlayPosition] = useState({ top: 200, right: 16 });
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const checkDesktop = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };

        checkDesktop();
        window.addEventListener('resize', checkDesktop);

        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    useEffect(() => {
        if (showFilters && typeof window !== 'undefined' && isDesktop) {
            const updatePosition = () => {
                // Find the search bar container (the relative parent)
                const searchContainer = document.querySelector('.relative.mb-10');
                if (searchContainer) {
                    const rect = searchContainer.getBoundingClientRect();
                    setOverlayPosition({
                        top: rect.bottom + 12,
                        right: window.innerWidth - rect.right,
                    });
                }
            };

            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        }
    }, [showFilters, isDesktop]);

    if (!showFilters) return null;

    const propertyTypesList = [
        { id: "Apartment", label: "Apartment", icon: Building2 },
        { id: "Unit", label: "Unit", icon: Building },
        { id: "House", label: "House", icon: Home },
        { id: "Townhouse", label: "Townhouse", icon: Building2 },
        { id: "Villa", label: "Villa", icon: Home },
        { id: "House and Land", label: "House and Land", icon: Map },
        { id: "Land", label: "Land", icon: Trees },
        { id: "Block of Units", label: "Block of Units", icon: Layers },
    ];

    const priceOptions = [
        { label: "Any", value: "Any" },
        { label: "$100k", value: "100000" },
        { label: "$200k", value: "200000" },
        { label: "$300k", value: "300000" },
        { label: "$400k", value: "400000" },
        { label: "$500k", value: "500000" },
        { label: "$600k", value: "600000" },
        { label: "$700k", value: "700000" },
        { label: "$800k", value: "800000" },
        { label: "$900k", value: "900000" },
        { label: "$1M", value: "1000000" },
        { label: "$1.25M", value: "1250000" },
        { label: "$1.5M", value: "1500000" },
        { label: "$1.75M", value: "1750000" },
        { label: "$2M", value: "2000000" },
        { label: "$2.5M", value: "2500000" },
        { label: "$3M", value: "3000000" },
        { label: "$4M", value: "4000000" },
        { label: "$5M", value: "5000000" },
        { label: "$10M", value: "10000000" },
    ];

    const togglePropertyType = (id) => {
        setAdvFilters((prev) => {
            if (id === "all") return { ...prev, propertyTypes: ["all"] };
            let next = prev.propertyTypes.filter((t) => t !== "all");
            if (next.includes(id)) {
                next = next.filter((t) => t !== id);
                if (next.length === 0) next = ["all"];
            } else {
                next.push(id);
            }
            return { ...prev, propertyTypes: next };
        });
    };

    const overlayContent = (
        <>
            {/* Mobile/Tablet: Full screen overlay */}
            <div className="lg:hidden fixed inset-0 bg-black/50 z-[9998]" onClick={() => setShowFilters(false)} />

            <div className="
                fixed 
                inset-0 lg:inset-auto
                lg:top-auto lg:right-auto lg:bottom-auto
                w-full lg:w-[380px] xl:w-[420px]
                h-full lg:h-auto lg:max-h-[min(65vh,calc(100vh-250px))]
                bg-white 
                lg:rounded-[20px] xl:rounded-[24px] 
                shadow-2xl
                overflow-hidden 
                border-0 lg:border border-gray-100 
                flex flex-col 
                font-sans
            " style={{
                    zIndex: 9999,
                    ...(isDesktop ? {
                        top: `${overlayPosition.top}px`,
                        right: `${overlayPosition.right}px`,
                    } : {})
                }}>
                {/* Header – compact on desktop, shrink-0 so footer stays visible */}
                <div className="bg-[#F4F4F4] shrink-0 p-4 sm:p-6 lg:px-4 lg:py-3 xl:px-5 xl:py-3.5">
                    <div className="flex justify-between items-center mb-1">
                        <h2 className="text-sm sm:text-[15px] lg:text-[13px] xl:text-sm font-bold text-gray-900 leading-none">Manual Search</h2>
                        <button
                            onClick={() => setShowFilters(false)}
                            className="p-1.5 sm:p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5 lg:w-4 lg:h-4 text-gray-400" />
                        </button>
                    </div>
                    {subtitle ? (
                        <p className="text-[10px] sm:text-xs lg:text-[10px] xl:text-xs text-gray-400 mb-3 sm:mb-5 lg:mb-3 font-medium">
                            {subtitle}
                        </p>
                    ) : null}

                    {/* Buyer Type Tabs */}
                    {showBuyerType && (
                    <div className="flex gap-2 sm:gap-3 lg:gap-2">
                        <button
                            onClick={() => setBuyerType("owner")}
                            className={`flex-1 flex items-center justify-between px-3 sm:px-4 lg:px-3 py-2.5 sm:py-3 lg:py-2 rounded-[16px] sm:rounded-[20px] lg:rounded-[14px] transition-all border ${buyerType === "owner"
                                ? "bg-[#55C5D0] border-[#55C5D0] text-white shadow-lg shadow-[#55C5D0]/10"
                                : "bg-white border-transparent text-[#36423F] hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-2 sm:gap-3 lg:gap-2">
                                <div
                                    className={`p-1.5 sm:p-2 lg:p-1.5 rounded-lg sm:rounded-xl flex items-center justify-center text-[#55C5D0] ${buyerType === "owner" ? "bg-white" : "bg-[#B9E6EB]"
                                        }`}
                                >
                                    <Home className="w-3 h-3 lg:w-2.5 lg:h-2.5" />
                                </div>
                                <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium leading-none">Owner Occupier</span>
                            </div>
                            {buyerType === "owner" ? (
                                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-white/70" />
                            ) : (
                                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-300" />
                            )}
                        </button>
                        <button
                            onClick={() => setBuyerType("investor")}
                            className={`flex-1 flex items-center justify-between px-2 sm:px-3 lg:px-2 py-2.5 sm:py-3 lg:py-2 rounded-[16px] sm:rounded-[20px] lg:rounded-[14px] transition-all border ${buyerType === "investor"
                                ? "bg-[#55C5D0] border-[#55C5D0] text-white shadow-lg shadow-[#55C5D0]/10"
                                : "bg-white border-transparent text-[#36423F] hover:bg-gray-50"
                                }`}
                        >
                            <div className="flex items-center gap-2 sm:gap-3 lg:gap-2">
                                <div
                                    className={`p-1.5 sm:p-2 lg:p-1.5 rounded-lg sm:rounded-xl flex items-center justify-center text-[#55C5D0] ${buyerType === "investor" ? "bg-white" : "bg-[#B9E6EB]"
                                        }`}
                                >
                                    <TrendingUp className="w-3 h-3 lg:w-2.5 lg:h-2.5" />
                                </div>
                                <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium leading-none">Investor</span>
                            </div>
                            {buyerType === "investor" ? (
                                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-white/70" />
                            ) : (
                                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-300" />
                            )}
                        </button>
                    </div>
                    )}
                </div>

                {/* Scrollable body – min-h-0 so only this scrolls; Clear/Apply stay visible */}
                <div className="p-4 sm:p-6 lg:p-4 xl:p-5 pt-3 sm:pt-4 lg:pt-3 pb-8 sm:pb-10 lg:pb-8 space-y-5 sm:space-y-6 lg:space-y-3 xl:space-y-4 flex-1 min-h-0 overflow-y-auto pr-2 custom-scrollbar">
                    {/* Suburb Search */}
                    {showLocationSearch && (
                    <div>
                        <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                            Search Suburb, postcode or state
                        </h3>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search by Suburb, postcode or state"
                                className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-4 sm:pl-6 lg:pl-3 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm text-gray-600 focus:ring-2 focus:ring-[#55C5D0]/20 outline-none placeholder:text-gray-300"
                                value={filters.suburb}
                                onChange={(e) =>
                                    setFilters((prev) => ({ ...prev, suburb: e.target.value }))
                                }
                            />
                        </div>
                    </div>
                    )}

                    <div>
                        <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-2 leading-none">
                            Property Type
                        </h3>
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-y-4 sm:gap-y-5 lg:gap-y-2 lg:gap-x-1.5 xl:gap-y-3 gap-x-2">
                            <div
                                className="flex flex-col items-center gap-1.5 sm:gap-2 lg:gap-1 group cursor-pointer"
                                onClick={() => togglePropertyType("all")}
                            >
                                <div
                                    className={`w-[50px] h-[44px] sm:w-[55px] sm:h-[48px] lg:w-[44px] lg:h-[38px] xl:w-[48px] xl:h-[42px] rounded-[8px] sm:rounded-[10px] lg:rounded-[8px] flex items-center justify-center border transition-all ${advFilters.propertyTypes.includes("all")
                                        ? "bg-white border-black shadow-md"
                                        : "bg-[#ECECEC] border-[#BDBDBD] group-hover:bg-white group-hover:border-gray-200"
                                        }`}
                                >
                                    <span className={`text-[9px] sm:text-[10px] lg:text-[8px] xl:text-[9px] font-bold ${advFilters.propertyTypes.includes("all") ? "text-black" : "text-gray-400"}`}>All</span>
                                </div>
                                <span className={`text-[9px] sm:text-[10px] lg:text-[8px] xl:text-[9px] font-bold text-center leading-tight ${advFilters.propertyTypes.includes("all") ? "text-black" : "text-gray-400"}`}>
                                    All Types
                                </span>
                            </div>
                            {propertyTypesList.map((type) => {
                                const Icon = type.icon;
                                return (
                                    <div
                                        key={type.id}
                                        className="flex flex-col items-center gap-1.5 sm:gap-2 lg:gap-1 group cursor-pointer"
                                        onClick={() => togglePropertyType(type.id)}
                                    >
                                        <div
                                            className={`w-[50px] h-[44px] sm:w-[55px] sm:h-[48px] lg:w-[44px] lg:h-[38px] xl:w-[48px] xl:h-[42px] rounded-[8px] sm:rounded-[10px] lg:rounded-[8px] flex items-center justify-center border transition-all ${advFilters.propertyTypes.includes(type.id)
                                                ? "bg-white border-black shadow-md"
                                                : "bg-[#ECECEC] border-[#BDBDBD] group-hover:bg-white group-hover:border-gray-200"
                                                }`}
                                        >
                                            <Icon
                                                className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-4 lg:h-4 xl:w-5 xl:h-5 transition-all ${advFilters.propertyTypes.includes(type.id)
                                                    ? "text-black"
                                                    : "text-gray-400"
                                                    }`}
                                            />
                                        </div>
                                        <span className={`text-[9px] sm:text-[10px] lg:text-[8px] xl:text-[9px] font-bold text-center leading-tight ${advFilters.propertyTypes.includes(type.id) ? "text-black" : "text-gray-400"}`}>
                                            {type.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Range Selectors */}
                    <div className="space-y-5 sm:space-y-6 lg:space-y-3 xl:space-y-4">
                        <div>
                            <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                Price Range (Budget)
                            </h3>
                            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-2">
                                <div className="space-y-1">
                                    <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase">
                                        Min
                                    </span>
                                    <div className="relative group">
                                        <select
                                            value={advFilters.priceRange.min}
                                            onChange={(e) =>
                                                setAdvFilters((prev) => ({
                                                    ...prev,
                                                    priceRange: { ...prev.priceRange, min: e.target.value },
                                                }))
                                            }
                                            className="w-full bg-[#F5F5F5] border-none rounded-lg sm:rounded-xl lg:rounded-lg py-3 sm:py-4 lg:py-2.5 pl-3 sm:pl-6 lg:pl-2.5 pr-8 sm:pr-10 lg:pr-7 text-[11px] sm:text-xs font-medium text-gray-700 appearance-none outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                                        >
                                            {priceOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-2 sm:right-4 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase">
                                        Max
                                    </span>
                                    <div className="relative">
                                        <select
                                            value={advFilters.priceRange.max}
                                            onChange={(e) =>
                                                setAdvFilters((prev) => ({
                                                    ...prev,
                                                    priceRange: { ...prev.priceRange, max: e.target.value },
                                                }))
                                            }
                                            className="w-full bg-[#F5F5F5] border-none rounded-lg sm:rounded-xl lg:rounded-lg py-3 sm:py-4 lg:py-2.5 pl-3 sm:pl-6 lg:pl-2.5 pr-8 sm:pr-10 lg:pr-7 text-[11px] sm:text-xs font-medium text-gray-700 appearance-none outline-none focus:ring-2 focus:ring-[#55C5D0]/20"
                                        >
                                            {priceOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-2 sm:right-4 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                Bedrooms
                            </h3>
                            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-2">
                                <div className="space-y-1">
                                    <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase">
                                        Min
                                    </span>
                                    <div className="relative">
                                        <select
                                            value={advFilters.bedrooms.min}
                                            onChange={(e) =>
                                                setAdvFilters((prev) => ({
                                                    ...prev,
                                                    bedrooms: { ...prev.bedrooms, min: e.target.value },
                                                }))
                                            }
                                            className="w-full bg-[#F5F5F5] border-none rounded-lg sm:rounded-xl lg:rounded-lg py-3 sm:py-4 lg:py-2.5 pl-3 sm:pl-6 lg:pl-2.5 pr-8 sm:pr-10 lg:pr-7 text-[11px] sm:text-xs font-medium text-gray-700 appearance-none outline-none"
                                        >
                                            <option>Any</option>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-2 sm:right-4 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase">
                                        Max
                                    </span>
                                    <div className="relative">
                                        <select
                                            value={advFilters.bedrooms.max}
                                            onChange={(e) =>
                                                setAdvFilters((prev) => ({
                                                    ...prev,
                                                    bedrooms: { ...prev.bedrooms, max: e.target.value },
                                                }))
                                            }
                                            className="w-full bg-[#F5F5F5] border-none rounded-lg sm:rounded-xl lg:rounded-lg py-3 sm:py-4 lg:py-2.5 pl-3 sm:pl-6 lg:pl-2.5 pr-8 sm:pr-10 lg:pr-7 text-[11px] sm:text-xs font-medium text-gray-700 appearance-none outline-none"
                                        >
                                            <option>Any</option>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <option key={n} value={n}>
                                                    {n}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-2 sm:right-4 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between py-1 sm:py-2 lg:py-0.5">
                        <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium text-gray-900 leading-none">
                            Select bedroom range
                        </span>
                        <button
                            onClick={() =>
                                setAdvFilters((prev) => ({
                                    ...prev,
                                    useBedroomRange: !prev.useBedroomRange,
                                }))
                            }
                            className="relative w-[44px] sm:w-[50px] lg:w-[42px] h-[22px] sm:h-[26px] lg:h-[20px] rounded-full border border-gray-100 p-[2px] transition-all outline-none bg-white ring-1 ring-black/5"
                        >
                            <div className={`w-full h-full rounded-full transition-colors duration-300 ${advFilters.useBedroomRange ? 'bg-[#55C5D0]' : 'bg-[#D9D9D9]'}`} />
                            <div className={`absolute top-[3px] left-[3px] w-[24px] sm:w-[28px] lg:w-[22px] h-4 sm:h-5 lg:h-3.5 bg-white rounded-full shadow-sm transition-transform duration-300 transform ${advFilters.useBedroomRange ? 'translate-x-[14px] sm:translate-x-[16px] lg:translate-x-[14px]' : 'translate-x-0'}`} />
                        </button>
                    </div>

                    <div className="space-y-5 sm:space-y-6 lg:space-y-3 xl:space-y-4">
                        <div>
                            <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                Bathrooms
                            </h3>
                            <div className="relative">
                                <select
                                    value={advFilters.bathrooms}
                                    onChange={(e) =>
                                        setAdvFilters((prev) => ({
                                            ...prev,
                                            bathrooms: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-4 sm:pl-6 lg:pl-3 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                >
                                    <option>Any</option>
                                    {[1, 2, 3, 4].map((n) => (
                                        <option key={n} value={n}>
                                            {n}+
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 sm:right-6 lg:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                        <div>
                            <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                Car Spaces
                            </h3>
                            <div className="relative">
                                <select
                                    value={advFilters.carSpaces}
                                    onChange={(e) =>
                                        setAdvFilters((prev) => ({
                                            ...prev,
                                            carSpaces: e.target.value,
                                        }))
                                    }
                                    className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-4 sm:pl-6 lg:pl-3 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                >
                                    <option>Any</option>
                                    {[1, 2, 3, 4].map((n) => (
                                        <option key={n} value={n}>
                                            {n}+
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 sm:right-6 lg:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                            Land Size
                        </h3>
                        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-2">
                            <div className="space-y-1 sm:space-y-1.5 lg:space-y-0.5">
                                <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium text-gray-900">
                                    Min
                                </span>
                                <div className="relative">
                                    <select
                                        value={advFilters.landSize.min}
                                        onChange={(e) =>
                                            setAdvFilters((prev) => ({
                                                ...prev,
                                                landSize: { ...prev.landSize, min: e.target.value },
                                            }))
                                        }
                                        className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-3 sm:pl-6 lg:pl-2.5 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                    >
                                        <option>Any</option>
                                        <option value="100">100 m²</option>
                                        <option value="500">500 m²</option>
                                        <option value="1000">1000 m²</option>
                                    </select>
                                    <ChevronDown className="absolute right-3 sm:right-5 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div className="space-y-1 sm:space-y-1.5 lg:space-y-0.5">
                                <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium text-gray-900">
                                    Max
                                </span>
                                <div className="relative">
                                    <select
                                        value={advFilters.landSize.max}
                                        onChange={(e) =>
                                            setAdvFilters((prev) => ({
                                                ...prev,
                                                landSize: { ...prev.landSize, max: e.target.value },
                                            }))
                                        }
                                        className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-3 sm:pl-6 lg:pl-2.5 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                    >
                                        <option>Any</option>
                                        <option value="500">500 m²</option>
                                        <option value="1000">1000 m²</option>
                                        <option value="5000">5000 m²+</option>
                                    </select>
                                    <ChevronDown className="absolute right-3 sm:right-5 lg:right-2 top-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 lg:w-3 lg:h-3 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {buyerType === "investor" && (
                        <div className="space-y-5 sm:space-y-6 lg:space-y-3 xl:space-y-4">
                            <div>
                                <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                    Target Rental Yield (%)
                                </h3>
                                <div className="relative">
                                    <select
                                        value={advFilters.targetYield}
                                        onChange={(e) =>
                                            setAdvFilters((prev) => ({
                                                ...prev,
                                                targetYield: e.target.value,
                                            }))
                                        }
                                        className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-4 sm:pl-6 lg:pl-3 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                    >
                                        <option>Any</option>
                                        {[3, 4, 5, 6, 7].map((n) => (
                                            <option key={n} value={n}>
                                                {n}%+
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-4 sm:right-6 lg:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                            <div>
                                <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-1.5 leading-none">
                                    Capital Growth (YoY %)
                                </h3>
                                <div className="relative">
                                    <select
                                        value={advFilters.capitalGrowth}
                                        onChange={(e) =>
                                            setAdvFilters((prev) => ({
                                                ...prev,
                                                capitalGrowth: e.target.value,
                                            }))
                                        }
                                        className="w-full bg-[#F5F5F5] border-none rounded-xl sm:rounded-2xl py-3 sm:py-4 lg:py-2.5 xl:py-3 pl-4 sm:pl-6 lg:pl-3 pr-10 sm:pr-12 lg:pr-9 text-xs sm:text-sm font-medium text-gray-700 appearance-none outline-none"
                                    >
                                        <option>Any</option>
                                        {[5, 10, 15, 20].map((n) => (
                                            <option key={n} value={n}>
                                                {n}%+
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className="absolute right-4 sm:right-6 lg:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>
                    )}

                    {showPriceOnlyToggle && (
                    <div
                        className="flex items-center gap-2 sm:gap-3 lg:gap-2 py-1 sm:py-2 lg:py-0.5 cursor-pointer group"
                        onClick={() =>
                            setAdvFilters((prev) => ({
                                ...prev,
                                showWithPriceOnly: !prev.showWithPriceOnly,
                            }))
                        }
                    >
                        <div
                            className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${advFilters.showWithPriceOnly
                                ? "bg-[#55C5D0] border-[#55C5D0]"
                                : "bg-white border-gray-200 group-hover:border-gray-300"
                                }`}
                        >
                            {advFilters.showWithPriceOnly && (
                                <Check className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 lg:w-2.5 lg:h-2.5 text-white" />
                            )}
                        </div>
                        <span className="text-[10px] sm:text-[11.47px] lg:text-[10px] font-medium text-gray-900 leading-none">
                            Only show properties with a price
                        </span>
                    </div>
                    )}

                    {showSaleMethod && (
                    <div>
                        <h3 className="text-sm sm:text-[15px] lg:text-xs xl:text-sm font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-2 leading-none">
                            {buyerType === "investor" ? "Sale Type" : "Sale Method"}
                        </h3>
                        <div className="flex bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden divide-x divide-gray-200">
                            {["All", "On Market", "Off Market", "Private Sale", "Auction"].map((method) => (
                                <button
                                    key={method}
                                    onClick={() =>
                                        setAdvFilters((prev) => ({ ...prev, saleMethod: method }))
                                    }
                                    className={`flex-1 py-2.5 sm:py-3 lg:py-2 xl:py-2.5 text-[9px] sm:text-[10px] lg:text-[9px] xl:text-[10px] font-medium transition-all ${advFilters.saleMethod === method
                                        ? "bg-[#F5F5F5] text-gray-900 font-bold"
                                        : "bg-white text-gray-500 hover:bg-gray-50"
                                        }`}
                                >
                                    {method}
                                </button>
                            ))}
                        </div>
                    </div>
                    )}

                    {/* <div
                    className="flex items-center gap-3 py-2 pb-6 cursor-pointer group"
                    onClick={() =>
                        setAdvFilters((prev) => ({
                            ...prev,
                            excludeUnderContract: !prev.excludeUnderContract,
                        }))
                    }
                >
                    <div
                        className={`w-5 h-5 rounded-[4px] border transition-all flex items-center justify-center ${advFilters.excludeUnderContract
                            ? "bg-[#55C5D0] border-[#55C5D0]"
                            : "bg-white border-gray-300 group-hover:border-gray-400"
                            }`}
                    >
                        {advFilters.excludeUnderContract && (
                            <Check className="w-3.5 h-3.5 text-white" />
                        )}
                    </div>
                    <span className="text-[11.47px] font-medium text-gray-900 leading-none">
                        Exclude Properties under contract/offer
                    </span>
                </div> */}
                </div>

                {/* Footer – shrink-0 so Clear/Apply always visible */}
                <div className="shrink-0 p-4 sm:p-6 lg:p-3 xl:p-4 pt-3 sm:pt-4 lg:pt-2 bg-white border-t border-gray-100 flex gap-2 sm:gap-3 lg:gap-2">
                    <button
                        onClick={() => {
                            if (onClear) onClear();
                        }}
                        className="flex-1 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 py-3 sm:py-4 lg:py-2.5 xl:py-3 rounded-xl sm:rounded-2xl lg:rounded-xl font-bold text-xs sm:text-sm lg:text-xs leading-none transition-all active:scale-[0.98]"
                    >
                        Clear Filters
                    </button>
                    <button
                        onClick={() => {
                            if (onApply) onApply();
                            setShowFilters(false);
                        }}
                        className="flex-[2] bg-[#55C5D0] hover:bg-[#4ab3bd] text-white py-3 sm:py-4 lg:py-2.5 xl:py-3 rounded-xl sm:rounded-2xl lg:rounded-xl font-bold text-xs sm:text-sm lg:text-xs leading-none transition-all shadow-lg shadow-[#55C5D0]/20 active:scale-[0.98]"
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </>
    );

    // Use portal for desktop to avoid overflow clipping issues
    if (typeof window !== 'undefined' && isDesktop && document.body) {
        return createPortal(overlayContent, document.body);
    }

    return overlayContent;
};

export default ManualSearchOverlay;
